<template>
  <v-dialog v-model="showDialog" max-width="720" persistent>
    <v-card>
      <v-card-title class="d-flex align-center">
        <span>Save Secrets</span>
        <v-spacer />
        <v-btn icon variant="text" @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-text>
        <v-alert v-if="submitError" type="error" density="compact" class="mb-3">{{ submitError }}</v-alert>
        <v-alert type="info" density="compact" variant="tonal" class="mb-4">
          These secret values are stored privately for your account and are not shared with other users.
        </v-alert>
        <div v-for="field in secretFields" :key="field.name" class="mb-4">
          <div class="text-subtitle-2">
            {{ field.label }}
            <span v-if="field.required" class="text-error">*</span>
          </div>
          <div class="text-caption mb-2">{{ field.description }}</div>
          <v-text-field
            v-model="values[field.name]"
            type="password"
            variant="outlined"
            density="compact"
            :label="field.label"
          />
        </div>
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn variant="text" @click="close">Cancel</v-btn>
        <v-btn color="primary" :loading="submitting" @click="submit">Save Secrets</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { UserVisibleMcpServer } from '@/types/mcp'
import { isApiError } from '@/types/api'
import { Options, Vue } from 'vue-class-component'

@Options({
  props: {
    show: Boolean,
    server: Object
  },
  emits: ['close', 'saved']
})
export default class ExternalMcpServerSecretsDialog extends Vue {
  show!: boolean
  server!: UserVisibleMcpServer | null
  values: Record<string, string> = {}
  submitting = false
  submitError = ''

  get showDialog(): boolean {
    return this.show
  }

  get secretFields() {
    if (!this.server) {
      return []
    }

    if ((this.server.secretFields?.length || 0) > 0) {
      return this.server.secretFields
    }

    return (this.server.secretNames || []).map((name) => ({
      name,
      label: name,
      description: '',
      required: true,
      inputType: 'password' as const
    }))
  }

  get configuredSecretNames(): string[] {
    return this.server?.configuredSecretNames || []
  }

  async submit(): Promise<void> {
    if (!this.server) return

    const configured = new Set(this.configuredSecretNames)
    const missingRequired = this.secretFields
      .filter((field) => field.required && !configured.has(field.name) && !(this.values[field.name] || '').trim())
      .map((field) => field.label || field.name)

    if (missingRequired.length > 0) {
      this.submitError = `Missing required secrets: ${missingRequired.join(', ')}`
      return
    }

    const secrets = Object.entries(this.values)
      .filter(([, value]) => value.trim().length > 0)
      .map(([name, value]) => ({ name, value }))

    this.submitting = true
    this.submitError = ''

    try {
      await this.$store.dispatch('saveExternalServerSecrets', {
        serverName: this.server.name,
        secrets
      })
      this.values = {}
      this.$emit('saved', this.server.name)
    } catch (error) {
      this.submitError = isApiError(error) ? error.message : error instanceof Error ? error.message : 'Failed to save secrets'
    } finally {
      this.submitting = false
    }
  }

  close() {
    this.$emit('close')
  }
}
</script>
